import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@primereact/ui/button';

import { ES_COLORS, ES_MODE_LIGHT, ES_MODES, ES_PRESETS, esSwatch, useEsTheme } from './EsThemeContext';
import { ES_SHADES, esColorName } from './EsTailwindPalette';
import { esAsHex, esFollowers, esResolveColor } from './EsThemeTokens';
import { VIEW_THEME } from './EsConstants';
import EsResetColors from './EsResetColors';
import EsSheetSwitch from './EsSheetSwitch';
import { useEsStrings } from './EsStrings';
import { useAuth } from '../views/parent/EsAuth';

/** A section title in the panel: small, because the panel's height has to fit a laptop's screen under the nav. */
const ES_MENU_TITLE = 'mb-1 text-xs font-medium leading-4';

/**
 * houndup's theme configurator, in escapade's nav bar.
 *
 * The same shape as ThemeConfig.tsx: a round trigger, a plain icon in the nav disc
 * (the ring of every hue moved to the account circle), and a panel under it holding
 * a light / dark / system row and, below it, grids of swatches. The grids are the quick
 * picks -- Background, Cards, Text, and Accent, the four semantic colors that paint the
 * most of the app (ES_QUICK_PICKS below); every other color is on Colors.
 *
 * The panel is a positioned div rather than a PrimeReact Popover, which in v11
 * is a set of parts that would have to be assembled in EsPrime.js first. It is
 * also what hupfront does, for the same reason it does it: this is a grid of
 * twenty-one 20px squares per grid and a mode row, and nothing in it is a component.
 * What IS PrimeReact is every Button here, which is also what guarantees the
 * theme's own stylesheet is on the page -- v11 injects it when the first
 * component mounts, not when the provider does (probed, EsPrimeProbe9), and the
 * nav bar is the one thing on every route.
 *
 * MOUNTED AND FADED rather than removed on close, the way houndup does it: a
 * panel that is unmounted cannot animate on the way out. pointer-events go with
 * the opacity, or an invisible panel still swallows the clicks meant for the
 * bar underneath.
 *
 * WHO MAY CHANGE WHAT. Anybody may open this and press anything. A signed-in
 * account's pick is its OWN (es_account_color); an admin's is a draft in this
 * browser, which reaches everyone only when they save the default colorsheet on
 * Colors; a signed-out reader's lasts the browser session. editDraft in
 * EsThemeContext routes it.
 *
 * The panel's order: Theme, the colorsheet list, the quick-pick grids, Preset, and
 * Reset -- beside Edit colors for a signed-out reader only. A signed-in reader reaches
 * the Colorsheet page from the account menu (EsNavBar's authRightItems); a signed-out
 * one has no account menu, and the page is public, so this is their way in.
 */
export default function EsThemeMenu() {
    const t = useEsStrings();
    // The menu's words in the reader's locale; the constants keep their English ids.
    const MODE_TEXT = {
        light: t( 'common-theme-mode-light', 'light' ),
        dark: t( 'common-theme-mode-dark', 'dark' ),
        system: t( 'common-theme-mode-system', 'system' ),
    };
    const PICK_TEXT = {
        background: t( 'common-theme-pick-background', 'Background' ),
        cards: t( 'common-theme-pick-cards', 'Cards' ),
        text: t( 'common-theme-pick-text', 'Text' ),
        accent: t( 'common-theme-pick-accent', 'Accent' ),
    };
    const navigate = useNavigate();
    const signedIn = Boolean( useAuth()?.authenticated );
    const { mode, preset, resolved, colors, aimed, editDraft, editTheme: pick } = useEsTheme();
    const [ open, setOpen ] = useState( false );
    const box = useRef( null );

    // Close on a click anywhere else, and on Escape. Both, because a panel that
    // can only be dismissed by pressing the button that opened it is a trap for
    // anyone not using a mouse.
    useEffect( () => {
        if ( !open ) return undefined;

        // The colorsheet list's popup is portalled to <body>, outside the panel: a
        // click on one of its options is a click in the menu, not beside it.
        const aClick = ( pEvent ) => {
            if ( pEvent.target.closest?.( '.p-select-popup' ) ) return;
            if ( box.current && !box.current.contains( pEvent.target ) ) setOpen( false );
        };
        const aKey = ( pEvent ) => {
            if ( pEvent.key === 'Escape' ) setOpen( false );
        };

        document.addEventListener( 'mousedown', aClick );
        document.addEventListener( 'keydown', aKey );
        return () => {
            document.removeEventListener( 'mousedown', aClick );
            document.removeEventListener( 'keydown', aKey );
        };
    }, [ open ] );

    return (
        <div ref={ box } className="relative">
            <button
                type="button"
                data-testid="es-theme-menu-trigger"
                aria-label={ t( 'common-theme', 'Theme' ) }
                aria-expanded={ open }
                onClick={ () => setOpen( ( pWas ) => !pWas ) }
                data-tip={ t( 'common-theme-tip', 'Colors' ) }
                className="esNavDisc flex cursor-pointer items-center justify-center rounded-full bg-nav-disc text-nav-glyph hover:bg-nav-disc-hover">
                <i className="pi pi-palette text-lg leading-none" />
            </button>

            <div
                data-testid="es-theme-menu"
                aria-hidden={ !open }
                className={ 'absolute inset-e-0 top-[calc(100%+0.5rem)] z-[99999] flex h-fit w-64 flex-col gap-2'
                    + ' rounded-lg border border-nav-menu-edge bg-panel'
                    + ' p-2.5 text-sm text-title shadow-lg transition-all ease-in-out'
                    + ( open ? ' opacity-100' : ' pointer-events-none opacity-0' ) }>

                { /* reachable says whether the panel is on screen. It is passed
                     down to every control rather than being read from `open`
                     there, because the panel is kept MOUNTED so it can fade --
                     and a closed panel that is merely invisible still puts
                     eighty-odd controls in the tab order of every page. */ }
                { /* Theme leads (Tim, 2026-10-11): every grid below shows its
                     colors at the step for the mode on screen, so the mode is
                     the pick the rest are read in. Its title is inline because
                     three buttons leave room; Preset's four do not. */ }
                <div className="flex items-center gap-2">
                    <div className="text-xs font-medium leading-4">{ t( 'common-theme', 'Theme' ) }</div>
                    { /* The chosen mode is a plain Button, which in Lara IS the
                         primary fill -- so this row is drawn in whatever accent
                         the Accent grid below is set to, and the two pickers answer
                         each other. The other two are outlined secondaries.
                         There is no severity="primary": the absence of a
                         severity is what primary means to the preset. */ }
                    <div data-testid="es-theme-modes" className="flex items-center gap-2">
                        { ES_MODES.map( ( aMode ) => (
                            <Button
                                key={ aMode }
                                data-testid={ 'es-theme-mode-' + aMode }
                                aria-pressed={ mode === aMode }
                                tabIndex={ open ? undefined : -1 }
                                severity={ mode === aMode ? undefined : 'secondary' }
                                variant={ mode === aMode ? undefined : 'outlined' }
                                size="small"
                                className="capitalize"
                                onClick={ () => pick( { mode: aMode } ) }>
                                { MODE_TEXT[ aMode ] ?? aMode }
                            </Button>
                        ) ) }
                    </div>
                </div>

                { /* The colorsheet under Theme, above the grids (Tim, 2026-10-11): it swaps
                     the whole look, and the grids below show whichever sheet is chosen. */ }
                <EsSheetSwitch data-testid="es-theme-menu-sheet-select" size="small" reachable={ open } />

                { ES_QUICK_PICKS.map( ( aPick ) => (
                    <EsSwatches
                        key={ aPick.id }
                        title={ PICK_TEXT[ aPick.id ] ?? aPick.title }
                        testIdPrefix={ 'es-theme-' + aPick.id }
                        reachable={ open }
                        mode={ resolved }
                        steps={ aPick.cells[ 0 ] }
                        chosen={ esQuickChosen( aPick, colors, resolved ) }
                        chosenStep={ esQuickStep( aPick, colors, resolved ) }
                        onPick={ ( pColor ) => editDraft( esQuickPatch( aPick, pColor ), esQuickCells( aPick, pColor, aimed.colors ) ) }
                        onStep={ ( pColor ) => editDraft( null, esQuickStepCells( aPick, pColor, resolved,
                            esQuickNextStep( esQuickStep( aPick, colors, resolved ) ) ) ) } />
                ) ) }

                { /* The PrimeReact preset under it all: shapes and shadows, not colors. */ }
                <div>
                    <div className={ ES_MENU_TITLE }>{ t( 'common-theme-preset', 'Preset' ) }</div>
                    <div data-testid="es-theme-presets" className="flex items-center gap-2">
                        { ES_PRESETS.map( ( aPreset ) => (
                            <Button
                                key={ aPreset }
                                data-testid={ 'es-theme-preset-' + aPreset }
                                aria-pressed={ preset === aPreset }
                                tabIndex={ open ? undefined : -1 }
                                severity={ preset === aPreset ? undefined : 'secondary' }
                                variant={ preset === aPreset ? undefined : 'outlined' }
                                size="small"
                                className="capitalize"
                                onClick={ () => pick( { preset: aPreset } ) }>
                                { /* es-copy: literal -- PrimeReact's preset names */ }
                                { aPreset }
                            </Button>
                        ) ) }
                    </div>
                </div>


                { /* Signed in, Reset has the row: the Colorsheet page is in the
                     account menu. Signed out there is no account menu, so Edit
                     colors stays beside it as the only way to that public page. */ }
                <div className={ 'grid ' + ( signedIn ? 'grid-cols-1' : 'grid-cols-2' ) + ' gap-2' }>
                    { !signedIn && (
                        <Button data-testid="es-theme-menu-editor-link"
                                severity="secondary" variant="outlined" size="small"
                                tabIndex={ open ? undefined : -1 }
                                onClick={ () => { setOpen( false ); void navigate( VIEW_THEME ); } }>
                            { t( 'common-theme-edit', 'Edit colors…' ) }
                        </Button>
                    ) }
                    <EsResetColors data-testid="es-theme-menu-reset" size="small" reachable={ open } />
                </div>
            </div>
        </div>
    );
}

/**
 * The quick picks: four of the semantic panel's colors, the ones that paint the most of
 * the app (Tim, 2026-10-24: "pick top four semantic colors and put it in palette menu").
 * Background is `background`, the page; Cards is `content-background`, every card,
 * sheet, and panel; Text is `text-color`, the ink of nearly everything; Accent is
 * `primary-color`, links, focus, the current section, the active turn, and the icons.
 * Every component keys to these, so one press moves all of it. The PrimeReact families
 * ride along: Cards moves the surface ramp, which paints fields, dropdowns, and dialogs;
 * Accent moves the primary ramp, which paints every button.
 *
 * A pick is a Tailwind family and sets the cell light AND dark, at the step listed -- a
 * page cannot share one hex between modes, but it can share a hue. It also takes back
 * every cell the draft holds that keys to the picked color, so nothing a sheet or an
 * earlier pick stated outright is left behind: a picked accent moves the links too. The
 * cells land in the draft the Colors page edits (editDraft), so Save lights,
 * copy-on-save, and Reset treat them like any other edit. The swatch shows the step in
 * the mode on screen, so the grid shows what you get.
 *
 * A cell is [ token, lightStep, darkStep ].
 */
export const ES_QUICK_PICKS = [
    { id: 'background', title: 'Background', cells: [ [ 'background', 100, 900 ] ] },
    { id: 'cards', title: 'Cards', family: 'surface', cells: [ [ 'content-background', 50, 800 ] ] },
    { id: 'text', title: 'Text', cells: [ [ 'text-color', 700, 200 ] ] },
    { id: 'accent', title: 'Accent', family: 'primary', cells: [ [ 'primary-color', 700, 300 ] ] },
];

/** A family at a step -- `600/99` carries its alpha -- as hex, or null. */
export const esQuickHex = ( pColor, pStep ) => {
    const [ aStep, aAlpha ] = String( pStep ).split( '/' );
    const aHex = esAsHex( pColor + '-' + aStep );
    return aHex ? aHex + ( aAlpha ?? '' ) : null;
};

/**
 * The cells a pick writes: its tokens, both modes, and a null for each cell pHeld (the
 * draft's colors) states that keys to one of them, so it follows the pick again.
 */
export const esQuickCells = ( pPick, pColor, pHeld = null ) => pPick.cells.flatMap( ( [ aToken, aLight, aDark ] ) => [
    { token: aToken, mode: 'light', hex: esQuickHex( pColor, aLight ) },
    { token: aToken, mode: 'dark', hex: esQuickHex( pColor, aDark ) },
    ...esFollowers( aToken ).filter( ( aCell ) => pHeld?.[ aCell.token ]?.[ aCell.mode ] )
        .map( ( aCell ) => ( { ...aCell, hex: null } ) ),
] );

/** The PrimeReact family a pick also moves, if any. */
export const esQuickPatch = ( pPick, pColor ) => ( pPick.family ? { [ pPick.family ]: pColor } : null );

/**
 * The family the pick's first token is in now, in the mode on screen, or null when
 * it is no Tailwind step -- white, or a hex somebody dragged to.
 */
export const esQuickChosen = ( pPick, pColors, pMode ) => {
    const aToken = pPick.cells[ 0 ][ 0 ];
    const aHex = esResolveColor( pColors, aToken, pMode );
    const aName = aHex && aHex.length === 7 ? esColorName( aHex ) : null;
    return aName ? aName.slice( 0, aName.lastIndexOf( '-' ) ) : null;
};

/**
 * The shades a chosen swatch steps through when pressed again (Tim, 2026-10-29): 100,
 * 200, and on to 950, then 100 again. 50 is left out because the cycle he named wraps
 * to 100; a pick that starts at 50 (Cards, light) steps to 100 first.
 */
export const ES_QUICK_CYCLE = ES_SHADES.filter( ( aShade ) => aShade >= 100 );

/** The Tailwind step the pick's first token is at now, in the mode on screen, or null. */
export const esQuickStep = ( pPick, pColors, pMode ) => {
    const aHex = esResolveColor( pColors, pPick.cells[ 0 ][ 0 ], pMode );
    const aName = aHex && aHex.length === 7 ? esColorName( aHex ) : null;
    return aName ? Number( aName.slice( aName.lastIndexOf( '-' ) + 1 ) ) : null;
};

/** The step after pStep in ES_QUICK_CYCLE, wrapping; the cycle's first for a step outside it. */
export const esQuickNextStep = ( pStep ) => {
    const aAt = ES_QUICK_CYCLE.indexOf( pStep );
    return ES_QUICK_CYCLE[ aAt < 0 ? 0 : ( aAt + 1 ) % ES_QUICK_CYCLE.length ];
};

/**
 * The cells a repeat press writes: the pick's tokens at pStep, in the mode on screen
 * only. The other mode keeps its own step, and the family is unchanged, so the
 * PrimeReact ramp and the cells keyed to the token stay as they are.
 */
export const esQuickStepCells = ( pPick, pColor, pMode, pStep ) => pPick.cells.map( ( [ aToken ] ) => (
    { token: aToken, mode: pMode, hex: esQuickHex( pColor, pStep ) } ) );

/**
 * One titled grid of colors, seven to a row, as in houndup's panel.
 *
 * The rows are closer than the columns: the columns are spaced to fill the panel's
 * width, and a gap taller than the 4px a chosen swatch's outline needs is height the
 * panel spends on nothing.
 *
 * The swatch is the color at the step this grid would paint in the mode on screen,
 * as an inline background -- the one place in this app an inline style is right:
 * Tailwind cannot generate a class from a value only known at run time.
 *
 * The test id is the prefix plus the color name -- es-theme-background-rose,
 * es-theme-accent-slate -- and the prefix is per grid rather than shared, because
 * the same twenty-one names appear in every grid and one id would address four buttons.
 */
function EsSwatches( { title, testIdPrefix, reachable, mode, steps, chosen, chosenStep, onPick, onStep } ) {
    const aPickStep = mode === ES_MODE_LIGHT ? steps[ 1 ] : steps[ 2 ];
    return (
        <div>
            <div className={ ES_MENU_TITLE }>{ title }</div>
            <div data-testid={ testIdPrefix + '-grid' } className="grid grid-cols-7 gap-x-2.5 gap-y-1.5">
                { ES_COLORS.map( ( aColor ) => {
                    // The chosen swatch shows the shade it is at, so a repeat press shows where it went.
                    const aStep = chosen === aColor && chosenStep ? chosenStep : aPickStep;
                    const aHex = esQuickHex( aColor, aStep ) ?? 'transparent';
                    return (
                        <button
                            key={ aColor }
                            type="button"
                            data-testid={ testIdPrefix + '-' + aColor }
                            data-tip={ aColor + '-' + String( aStep ).split( '/' )[ 0 ] }
                            aria-label={ title + ': ' + aColor }
                            aria-pressed={ chosen === aColor }
                            tabIndex={ reachable ? undefined : -1 }
                            onClick={ () => ( chosen === aColor ? onStep( aColor ) : onPick( aColor ) ) }
                            className={ 'h-5 w-5 cursor-pointer rounded-md border border-edge outline-offset-2'
                                + ( chosen === aColor ? ' outline outline-2' : ' outline-0 focus:outline focus:outline-1' ) }
                            style={ {
                                backgroundColor: aHex,
                                outlineColor: esSwatch( aColor ),
                            } } />
                    );
                } ) }
            </div>
        </div>
    );
}
